import { createSlice } from '@reduxjs/toolkit';
import { setWith } from 'lodash';
import { ControlUnitAPIResponse } from '../../../service/controlUnit/ControlUnit.model';
import { DeviceControlUnitsAPIResponse } from '../../../service/device/device.model';
import {
  DigitalChannelEvent,
  SensordataNewest,
  UUID,
} from '../../../service/monitoring/monitoring.model';
import { arrayToRecord } from '../../../shared/ArrayUtil';
import { RecordLVL2, RecordLVL3 } from '../../../shared/types/Records';

import {
  fetchCurrentEventsControlUnits,
  fetchCurrentSensorDataControlUnits,
} from '../actions/asyncThunks';
import { nameReducer } from '../controlUnit.model';
import ControlUnitAPI from '../ControlUnitAPI';

export const initialStateControlUnits: ControlUnitState = {
  dictionaryControlUnit: {},
  dictionaryCurrentEventsControlUnit: {},
  loadingCurrentEventsControlUnit: {},
  errorCurrentEventsControlUnit: {},
  dictionaryCurrentSensorDataControlUnit: {},
  loadingCurrentSensorDataControlUnit: {},
  errorCurrentSensorDataControlUnit: {},
};

type LinkedDigitalEvent = RecordLVL3<
  UUID,
  ControlUnitAPIResponse['id'],
  DigitalChannelEvent['eventID'],
  DigitalChannelEvent
>;

type LinkedDigitalSensorData = RecordLVL3<
  UUID,
  ControlUnitAPIResponse['id'],
  SensordataNewest['channel'],
  SensordataNewest
>;

export interface ControlUnitState {
  dictionaryControlUnit: Record<
    UUID,
    | Record<DeviceControlUnitsAPIResponse['id'], DeviceControlUnitsAPIResponse>
    | undefined
  >;
  // Current Events
  dictionaryCurrentEventsControlUnit: LinkedDigitalEvent;
  loadingCurrentEventsControlUnit: Record<UUID, boolean | undefined>;
  errorCurrentEventsControlUnit: Record<UUID, any | undefined>;
  // Current SensorData
  dictionaryCurrentSensorDataControlUnit: LinkedDigitalSensorData;
  loadingCurrentSensorDataControlUnit: Record<UUID, boolean | undefined>;
  errorCurrentSensorDataControlUnit: Record<UUID, any | undefined>;
}

export const ControlUnitSlice = createSlice({
  name: nameReducer,
  initialState: initialStateControlUnits,
  reducers: {},
  extraReducers: (builder) => {
    // fetchCurrentAlarmsControlUnits Thunk
    builder.addCase(fetchCurrentEventsControlUnits.pending, (state, action) => {
      state.loadingCurrentEventsControlUnit[action.meta.arg.deviceId] = true;
      state.errorCurrentEventsControlUnit[action.meta.arg.deviceId] = undefined;
    });
    builder.addCase(
      fetchCurrentEventsControlUnits.fulfilled,
      (state, action) => {
        // fulfilled are in other action
        state.loadingCurrentEventsControlUnit[action.meta.arg.deviceId] = false;
        state.errorCurrentEventsControlUnit[action.meta.arg.deviceId] =
          undefined;
        if (action.payload)
          state.dictionaryCurrentEventsControlUnit[action.meta.arg.deviceId] =
            arrayToRecord(action.payload, 'idCU', (cuEvents) =>
              arrayToRecord(cuEvents.events, 'eventID')
            );
      }
    );

    builder.addCase(
      fetchCurrentEventsControlUnits.rejected,
      (state, action) => {
        state.loadingCurrentEventsControlUnit[action.meta.arg.deviceId] = false;
        state.errorCurrentEventsControlUnit[action.meta.arg.deviceId] =
          action.error;
      }
    );

    // fetchCurrentSensorDataControlUnits Thunk
    builder.addCase(
      fetchCurrentSensorDataControlUnits.pending,
      (state, action) => {
        state.loadingCurrentSensorDataControlUnit[action.meta.arg] = true;
        state.errorCurrentSensorDataControlUnit[action.meta.arg] = undefined;
      }
    );
    builder.addCase(
      fetchCurrentSensorDataControlUnits.fulfilled,
      (state, action) => {
        // fulfilled are in other action
        state.loadingCurrentSensorDataControlUnit[action.meta.arg] = false;
        state.errorCurrentSensorDataControlUnit[action.meta.arg] = undefined;
        if (action.payload)
          state.dictionaryCurrentSensorDataControlUnit[action.meta.arg] =
            action.payload.reduce((acc, currentItem) => {
              setWith(
                acc,
                [currentItem.cuId, currentItem.channel],
                currentItem,
                Object
              );
              return acc;
            }, {} as RecordLVL2<ControlUnitAPIResponse['id'], SensordataNewest['channel'], SensordataNewest>);
      }
    );

    builder.addCase(
      fetchCurrentSensorDataControlUnits.rejected,
      (state, action) => {
        state.loadingCurrentEventsControlUnit[action.meta.arg] = false;
        state.errorCurrentEventsControlUnit[action.meta.arg] = action.error;
      }
    );

    // Sync ControlUnitAPI fulfilled data into legacy dictionaryControlUnit
    // TODO: Remove this when all components are migrated to use ControlUnitAPI directly
    // Pending: src\service\monitoring\util\mapEventsDigitalToSTCharTimeLine.ts
    builder.addMatcher(
      ControlUnitAPI.endpoints.getControlUnitsByDevice.matchFulfilled,
      (state, action) => {
        const { deviceId } = action.meta.arg.originalArgs;
        if (action.payload)
          state.dictionaryControlUnit[deviceId] = arrayToRecord(
            action.payload.controlunits,
            'id'
          );
      }
    );
  },
});

export const {
  actions: actionsControlUnit,
  reducer: reducerControlUnit,
  name: nameReducerControlUnit,
} = ControlUnitSlice;
